import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = '/api';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' }
];

const ContactSection = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', state: '', message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(t('toast_success'));
        setFormData({ name: '', email: '', phone: '', state: '', message: '' });
      }
    } catch {
      toast.error(t('toast_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-emerald-50">
      <div className="container mx-auto">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">{t('contact_title')}</h2>
            <p className="text-xl text-slate-600">{t('contact_subtitle')}</p>
            <p className="text-base text-amber-700 font-medium mt-3">{t('contact_bilingual')}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <Card className="shadow-xl border-2 border-slate-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">{t('contact_form_title')}</h3>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('contact_name')}</label>
                    <Input data-testid="contact-name-input" type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder={t('contact_name_placeholder')} required className="border-slate-300 focus:border-amber-600 focus:ring-amber-600" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('contact_email')}</label>
                    <Input data-testid="contact-email-input" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder={t('contact_email_placeholder')} required className="border-slate-300 focus:border-amber-600 focus:ring-amber-600" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('contact_phone')}</label>
                    <Input data-testid="contact-phone-input" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder={t('contact_phone_placeholder')} required className="border-slate-300 focus:border-amber-600 focus:ring-amber-600" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('contact_state')}</label>
                    <Select value={formData.state} onValueChange={(value) => setFormData({...formData, state: value})} required>
                      <SelectTrigger className="border-slate-300 focus:border-amber-600 focus:ring-amber-600" data-testid="contact-state-select">
                        <SelectValue placeholder={t('contact_state_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('contact_message')}</label>
                    <Textarea data-testid="contact-message-input" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder={t('contact_message_placeholder')} rows={4} required className="border-slate-300 focus:border-amber-600 focus:ring-amber-600" />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 text-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit-button">
                    {isSubmitting ? t('contact_submitting') : t('contact_submit')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-8">
              <Card className="shadow-xl border-2 border-slate-200 hover:border-emerald-700 transition-colors duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-7 h-7 text-emerald-800" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{t('contact_email_us')}</h3>
                      <p className="text-slate-600 mb-2">{t('contact_email_response')}</p>
                      <a href="mailto:ivonne@solvidafinancial.com" className="text-xl font-semibold text-emerald-800 hover:text-emerald-900 transition-colors duration-200">
                        ivonne@solvidafinancial.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
